import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Switch,
  FormControlLabel
} from '@mui/material';
import { SettingOutlined, EditOutlined, ClearOutlined } from '@ant-design/icons';
import OrderGridColumnSettingsDialog from './OrderGridColumnSettingsDialog';
import useOrderGridSettings from 'hooks/useOrderGridSettings';
import useEnhancedGrid from 'hooks/useEnhancedGrid';
import { ColumnSetting } from './OrderGridColumnSettingsDialog';

interface OrderGridSettingsSectionProps {
  onSettingsChange?: () => void;
}

export default function OrderGridSettingsSection({ onSettingsChange }: OrderGridSettingsSectionProps) {
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const { columnSettings, saveColumnSettings, clearColumnSettings, forceUpdate } = useOrderGridSettings();
  const { enhancedGridEnabled, setEnhancedGridEnabled } = useEnhancedGrid();

  const handleEditSettings = () => {
    setSettingsDialogOpen(true);
  };

  const handleClearSettings = () => {
    clearColumnSettings();
    // Force immediate update
    forceUpdate();
    onSettingsChange?.();
    
    // Dispatch custom event for immediate grid update
    window.dispatchEvent(new CustomEvent('orderGridSettingsChanged', { 
      detail: { settings: 'cleared' } 
    }));
    
    // Also trigger a small delay to ensure the grid updates
    setTimeout(() => {
      onSettingsChange?.();
    }, 100);
  };

  const handleSaveSettings = (settings: ColumnSetting[]) => {
    saveColumnSettings(settings);
    // Force immediate update
    forceUpdate();
    onSettingsChange?.();
    
    // Dispatch custom event for immediate grid update
    window.dispatchEvent(new CustomEvent('orderGridSettingsChanged', { 
      detail: { settings } 
    }));
    
    // Also trigger a small delay to ensure the grid updates
    setTimeout(() => {
      onSettingsChange?.();
    }, 100);
  };

  const handleEnhancedGridToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEnhancedGridEnabled(event.target.checked);
    onSettingsChange?.();
    
    // Dispatch custom event for immediate grid update
    window.dispatchEvent(new CustomEvent('orderGridSettingsChanged', { 
      detail: { enhancedGrid: event.target.checked } 
    }));
    
    // Also trigger a small delay to ensure the grid updates
    setTimeout(() => {
      onSettingsChange?.();
    }, 100);
  };

  const visibleColumnsCount = columnSettings.filter((col: ColumnSetting) => col.visible).length;
  const totalColumnsCount = columnSettings.length;

  return (
    <>
      <Divider sx={{ my: 2 }} />
      
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          Enhanced Grid
        </Typography>
        
        <FormControlLabel
          control={
            <Switch
              checked={enhancedGridEnabled}
              onChange={handleEnhancedGridToggle}
              color="primary"
            />
          }
          label={enhancedGridEnabled ? 'Enabled' : 'Disabled'}
        />
      </Box>

      <Divider sx={{ my: 2 }} />
      
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          Orders Grid Fields
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {visibleColumnsCount} of {totalColumnsCount} columns visible
        </Typography>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<EditOutlined />}
            onClick={handleEditSettings}
            sx={{ flex: 1 }}
          >
            Edit
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<ClearOutlined />}
            onClick={handleClearSettings}
            sx={{ flex: 1 }}
          >
            Clear
          </Button>
        </Box>
      </Box>

      <OrderGridColumnSettingsDialog
        open={settingsDialogOpen}
        onClose={() => setSettingsDialogOpen(false)}
        onSave={handleSaveSettings}
        initialColumns={columnSettings}
      />
    </>
  );
} 